import axios from 'axios'

/**
 * Unauthenticated axios instance for public endpoints.
 *
 * The default apiClient (client.ts) injects the Authorization header from the
 * auth store, which would cause a 401 on endpoints that don't accept tokens.
 * Public form pages (inquiry, gallery) use this client instead.
 */
/**
 * The one place the public API prefix is written down. `publicClient` applies it to
 * every axios call automatically, so feature code normally never sees it.
 */
export const PUBLIC_API_BASE_URL = '/api/v1'

export const publicClient = axios.create({
  baseURL: PUBLIC_API_BASE_URL,
})

/**
 * Origin-relative URL for a public API path that the BROWSER fetches directly —
 * an `<img src>`, an `<a href>`, a `window.open`. These are the only public-surface
 * fetches axios does not build for us, which is exactly how they drift: the Swiss
 * QR-bill `<img>` on the invoice pay page hardcoded `/invoices/pay/{token}/qr-bill.svg`
 * with no `/api/v1`, so it silently resolved to the SPA's index.html fallback (200,
 * text/html) on every invoice in every environment, `onError` fired, and the whole
 * QR block unmounted. Reading `publicClient.defaults.baseURL` keeps the two in lockstep
 * by construction rather than by convention.
 *
 * @param path API path relative to the public base, e.g. `/invoices/pay/abc/qr-bill.svg`.
 */
export function publicApiUrl(path: string): string {
  const base = publicClient.defaults.baseURL ?? PUBLIC_API_BASE_URL
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Normalise a public share route param — a token OR a gallery slug — ONCE, at the page
 * boundary, so every URL built from it downstream sees the same value.
 *
 * Share links are copy-pasted out of email and chat apps, which routinely bring a
 * trailing space along, and that space is invisible in a particularly nasty way. The
 * browser strips a trailing space when it resolves the DOCUMENT URL, so React Router
 * still hands the page a param that carries it — and the axios view request, where the
 * space lands at the very END of the URL, gets it stripped again and quietly resolves
 * to the real token (200). The SAME space sitting MID-path in
 * `/invoices/pay/{token}/qr-bill.svg` is not stripped: it is encoded as `%20`, 404s
 * against the backend's (correct, strict) token check, and `onError` unmounts the whole
 * QR block with no explanation. That is QA round 7 F8 — the F1 failure mode returning
 * through a different trigger, on the one payment aid the page exists for.
 *
 * Encoding is not cosmetic. A token that is still wrong AFTER trimming must stay inside
 * a single path segment, so it reaches the token check and comes back as the neutral
 * not-found page instead of reshaping the request path.
 *
 * The gallery is the same story with a slug rather than a token: `/{slug}` ends with it and
 * survives, while `/{slug}/favorite-lists`, `/{slug}/favorites/{id}` and `/{slug}/selection/submit`
 * all carry it mid-path. A client would browse the gallery normally and have every favourite
 * and the final selection submit fail. Hence "path param", not "token", in the name — one
 * implementation, because a second one is exactly how the two drift apart.
 *
 * @returns the URL-ready value, or null when nothing usable is left (e.g. all whitespace).
 */
export function normalizePublicPathParam(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim()
  return trimmed ? encodeURIComponent(trimmed) : null
}
